import { ROLE_PRIORITY, type AppRole } from './enums';
import type { UserRoleShape } from './schemas';

/**
 * Pure role-check helpers. Live in `@eduborg/shared` because the web client
 * needs them for nav-gating UX (which links to render) and the api Worker
 * needs them for authz (which routes to allow).
 *
 * The shapes are conservative on purpose: they take a `readonly UserRoleShape[]`
 * and return booleans. No DB dependency, no async — call sites pass already-
 * loaded role arrays.
 */

export function hasRole(roles: readonly UserRoleShape[], role: AppRole): boolean {
  return roles.some((r) => r.role === role);
}

export function hasOrgRole(roles: readonly UserRoleShape[], orgId: string, role: AppRole): boolean {
  if (hasRole(roles, 'super_admin')) return true;
  return roles.some((r) => r.role === role && r.orgId === orgId);
}

export function isOrgMember(roles: readonly UserRoleShape[], orgId: string): boolean {
  if (hasRole(roles, 'super_admin')) return true;
  return roles.some((r) => r.orgId === orgId);
}

export function getActiveRole(roles: readonly UserRoleShape[], orgId?: string): AppRole | null {
  const candidates = orgId === undefined ? roles : roles.filter((r) => r.orgId === orgId);
  if (candidates.length === 0) return null;

  let best: AppRole = candidates[0]!.role;
  for (const r of candidates) {
    if (ROLE_PRIORITY[r.role] > ROLE_PRIORITY[best]) best = r.role;
  }
  return best;
}

export function meetsMinRole(
  roles: readonly UserRoleShape[],
  orgId: string | undefined,
  minRole: AppRole,
): boolean {
  if (hasRole(roles, 'super_admin')) return true;
  const active = getActiveRole(roles, orgId);
  if (active === null) return false;
  return ROLE_PRIORITY[active] >= ROLE_PRIORITY[minRole];
}
